/** Pixa — sistem sayfaları: Entegrasyonlar, Raporlar. */
import React, { useState, useEffect } from 'react';
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts';
import {
  API, PAGE_SIZE, T, MONO, FONT, Badge, Button, Card, Col, DataTable, Eyebrow, Grid,
  Tabs, Toast, axios, fmtDate, fmtDateTime, fmtN, inputStyle, money, useData, useEnums,
  useIntegrations, useLang, useList,
} from './shared';
import { TabbedList } from './Stock';
import { HIDDEN_KEYS, RETURN_STATUS_TR, isDateField, isImageField, labelOf, sectionOf,
  titleCase, translateValue, valueKind } from './reportLabels';
import { useIsMobile } from '../poyais-ui/hooks';
import { goToErrorLog, errorLogTarget, takeFocus } from './focus';
import { MarketMark } from './marketplaces';
import { HealthReport } from './Findings';
import PoyaisSpinner from '../PoyaisSpinner';

// ── Entegrasyonlar ───────────────────────────────────────────────────────────
// "Bağlantıyı Test Et" Pixa'nın /integrations/check ucunu kullanmıyor: o uç sorunsuz
// çalışan kanallar için bile {ok:false} döndürüyor ve eski ekran HTTP 200'ü "Bağlantı
// başarılı" sanıyordu. Yerine veriye dayalı /integrations/health karnesi (HealthReport).
export function IntegrationsPage({ clientId }: { clientId: number }) {
  const { t } = useLang();
  const integrations = useIntegrations(clientId, false);
  const [open, setOpen] = useState<Set<string>>(new Set());
  const toggle = (n: string) => setOpen(s => { const x = new Set(s); x.has(n) ? x.delete(n) : x.add(n); return x; });
  const sorted = [...integrations].sort((a: any, b: any) =>
    Number(b.isMarketplace !== false) - Number(a.isMarketplace !== false) || Number(!!b.active) - Number(!!a.active));

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14, fontFamily: FONT }}>
      <Card pad={18}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' as const }}>
          <div style={{ flex: 1, minWidth: 240 }}>
            <div style={{ fontSize: 14, fontWeight: 800, color: T.text }}>{t('Bağlı Kanallar')}</div>
            <div style={{ fontSize: 12, color: T.textMid, marginTop: 4, lineHeight: 1.55 }}>
              {t('Bağlantı testi; entegrasyonun Pixa\'da açık olup olmadığını, son sipariş zamanını, son pazaryeri görevlerini ve son 24 saatin hata kayıtlarını birlikte kontrol eder.')}
            </div>
          </div>
          <Button variant="soft" color={T.accent} onClick={() => setOpen(new Set(sorted.filter((i: any) => i.active).map((i: any) => i.resourceName)))}
            style={{ padding: '7px 14px', fontSize: 12 }}>{t('Tümünü Test Et')}</Button>
        </div>
      </Card>
      <Grid min={300} gap={12}>
        {sorted.map((i: any) => {
          const market = i.isMarketplace !== false;
          return (
            <Card key={i.resourceId} pad={16} style={{ opacity: i.active ? 1 : 0.7 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
                <MarketMark name={i.resourceName} size={34} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 14, fontWeight: 800, color: T.text }}>{i.resourceName}</div>
                  <div style={{ fontSize: 11, color: T.textDim }}>{market ? t('Satış kanalı') : t('ERP / kargo entegrasyonu')}</div>
                </div>
                <Badge color={i.active ? T.green : T.textDim}>{i.active ? t('Aktif') : t('Pasif')}</Badge>
              </div>
              {open.has(i.resourceName)
                ? <HealthReport compact clientId={clientId} platform={i.resourceName} onClose={() => toggle(i.resourceName)} />
                : <Button variant="ghost" color={T.accent} onClick={() => toggle(i.resourceName)} style={{ padding: '6px 12px', fontSize: 11.5 }}>{t('Bağlantıyı Test Et')}</Button>}
            </Card>
          );
        })}
      </Grid>
      {integrations.length === 0 && <Card pad={28}><div style={{ textAlign: 'center' as const, fontSize: 12.5, color: T.textDim }}>{t('Kayıt bulunamadı.')}</div></Card>}
    </div>
  );
}


// ── Raporlar ─────────────────────────────────────────────────────────────────
const REPORTS = [
  { id: 'basics', label: 'Temel Sayılar', path: '/reports/basics', dated: false },
  { id: 'today', label: 'Bugün', path: '/reports/today', dated: false },
  { id: 'sales-by-city', label: 'Şehir Bazlı Satış', path: '/reports/sales-by-city', dated: true },
  { id: 'sales-chart', label: 'Satış Grafiği', path: '/reports/sales-chart', dated: true },
  { id: 'order-status', label: 'Sipariş Durumları', path: '/reports/order-status', dated: true },
  { id: 'claims-status', label: 'İade Durumları', path: '/reports/claims-status', dated: true },
  { id: 'dashboard-sales', label: 'Satış Panosu', path: '/reports/dashboard-sales', dated: true },
  { id: 'dashboard-products', label: 'Ürün Panosu', path: '/reports/dashboard-products', dated: true },
  { id: 'dashboard-finance', label: 'Finans Panosu', path: '/reports/dashboard-finance', dated: true },
];



/**
 * Bazı raporlar ham satır döndürüyor (şehir bazlı satış her sipariş için bir satır,
 * sipariş durumları her bayrak kombinasyonu için ayrı satır). Ekranda anlamlı olması
 * için bu raporlar gruplanıp toplanıyor.
 */
const AGGREGATIONS: Record<string, { by: string; sum: string[]; count?: string; labelOf?: (v: any, enums: any) => string }> = {
  'sales-by-city': { by: 'city', sum: ['quantity', 'generalTotal'], count: 'orderCount' },
  'order-status': { by: 'status', sum: ['total'], labelOf: (v, en) => (en?.status || {})[String(v)] || `Durum ${v}` },
  'claims-status': { by: 'returnStatus', sum: [], count: 'orderCount', labelOf: v => RETURN_STATUS_TR[String(v)] || `Durum ${v}` },
};

/** İç içe tek elemanlı nesneleri satıra düzleştirir (addresses:[{city}] → city). */
function flattenRow(row: any): any {
  if (!row || typeof row !== 'object') return row;
  const out: any = {};
  for (const [k, v] of Object.entries(row)) {
    if (Array.isArray(v) && v.length === 1 && v[0] && typeof v[0] === 'object') {
      for (const [ik, iv] of Object.entries(v[0])) if (!(ik in out)) out[ik] = iv;
    } else if (v && typeof v === 'object' && !Array.isArray(v)) {
      for (const [ik, iv] of Object.entries(v)) if (typeof iv !== 'object' && !(ik in out)) out[ik] = iv;
    } else {
      out[k] = v;
    }
  }
  return out;
}

function aggregate(rows: any[], conf: any, enums: any): any[] {
  const map = new Map<string, any>();
  for (const raw of rows) {
    const r = flattenRow(raw);
    const rawVal = r[conf.by];
    // Şehir adları Pixa'da hem "İstanbul" hem "İSTANBUL" olarak geliyor; aynı
    // gruba düşsünler diye anahtar büyük/küçük harften bağımsız normalleştiriliyor.
    const key = typeof rawVal === 'string'
      ? rawVal.toLocaleLowerCase('tr').trim()
      : String(rawVal ?? '—');
    if (!map.has(key)) {
      const label = conf.labelOf ? conf.labelOf(rawVal, enums)
        : typeof rawVal === 'string' ? titleCase(rawVal.toLocaleLowerCase('tr').trim()) : key;
      const base: any = { [conf.by]: label };
      for (const s of conf.sum) base[s] = 0;
      if (conf.count) base[conf.count] = 0;
      map.set(key, base);
    }
    const acc = map.get(key);
    for (const s of conf.sum) acc[s] += Number(r[s] || 0);
    if (conf.count) acc[conf.count] += 1;
  }
  const sortKey = conf.sum[conf.sum.length - 1] || conf.count;
  return Array.from(map.values()).sort((a, b) => Number(b[sortKey] || 0) - Number(a[sortKey] || 0));
}

export function ReportsPage({ clientId, start, end }: { clientId: number; start?: string; end?: string }) {
  const { t } = useLang();
  const isMobile = useIsMobile();
  const [sel, setSel] = useState('basics');
  const enums = useEnums(clientId);
  const rep = REPORTS.find(r => r.id === sel)!;
  const { data, loading, error } = useData(clientId, rep.path,
    rep.dated ? { start_date: start, end_date: end } : {});
  const currency = (data as any)?.currency || 'TRY';

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14, fontFamily: FONT }}>
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' as const }}>
        {REPORTS.map(r => (
          <Button key={r.id} variant={sel === r.id ? 'soft' : 'ghost'} color={T.accent} onClick={() => setSel(r.id)}
            style={{ padding: '5px 12px', fontSize: 11, fontWeight: sel === r.id ? 700 : 500 }}>{t(r.label)}</Button>
        ))}
      </div>
      {loading ? <Card pad={28}><PoyaisSpinner size={26} pad={10}/></Card>
        : error ? <Card pad={20}><div style={{ fontSize: 12, color: T.redText }}>{error}</div></Card>
        : <ReportView data={data} currency={currency} isMobile={isMobile} title={rep.label}
            aggregation={AGGREGATIONS[rep.id]} enums={enums} />}
    </div>
  );
}

function fmtVal(key: string, v: any, currency: string) {
  if (v === null || v === undefined || v === '') return '—';
  if (typeof v === 'boolean') return v ? 'Evet' : 'Hayır';
  if (isDateField(key, v)) return fmtDateTime(v);
  const kind = valueKind(key, v);
  if (kind === 'text') return translateValue(v);
  const n = Number(v);
  if (isNaN(n)) return translateValue(v);
  if (kind === 'money') return money(n, currency);
  if (kind === 'pct') return `%${n.toLocaleString('tr-TR', { maximumFractionDigits: 2 })}`;
  return fmtN(Math.round(n * 100) / 100);
}

function isPlainNum(v: any) {
  return typeof v === 'number' || (typeof v === 'string' && v !== '' && !isNaN(Number(v)));
}

function MetricCards({ obj, currency, isMobile }: { obj: any; currency: string; isMobile: boolean }) {
  const entries = Object.entries(obj || {})
    .filter(([k, v]) => !HIDDEN_KEYS.has(k) && k !== 'currency' && (isPlainNum(v) || typeof v === 'boolean'));
  if (!entries.length) return null;
  return (
    <Grid min={isMobile ? 140 : 180} gap={12}>
      {entries.map(([k, v]) => (
        <Card key={k} pad={16}>
          <Eyebrow color={T.accent}>{titleCase(labelOf(k))}</Eyebrow>
          <div style={{ fontSize: 19, fontWeight: 800, color: T.text, fontFamily: MONO, marginTop: 6 }}>
            {fmtVal(k, v, currency)}
          </div>
        </Card>
      ))}
    </Grid>
  );
}

const ROWS_PER_PAGE = 25;

/** Dizi bölümü — düzleştirme, sayfalama, grafik ve Türkçe başlıklı tablo. */
function ArraySection({ name, rows: rawRows, currency, isMobile, aggregation, enums }: {
  name: string; rows: any[]; currency: string; isMobile: boolean; aggregation?: any; enums?: any;
}) {
  const { t } = useLang();
  const [page, setPage] = useState(0);
  const rows = React.useMemo(() => {
    if (!rawRows.length) return [];
    if (aggregation) return aggregate(rawRows, aggregation, enums);
    return rawRows.map(flattenRow);
  }, [rawRows, aggregation, enums]);
  React.useEffect(() => { setPage(0); }, [rows.length, name]);

  if (!rows.length) return null;
  if (!rows.every(r => r && typeof r === 'object')) {
    return (
      <div>
        <div style={{ fontSize: 12.5, fontWeight: 700, color: T.text, marginBottom: 8 }}>{titleCase(sectionOf(name))}</div>
        <div style={{ fontSize: 12, color: T.textMid }}>{rows.slice(0, 20).map(String).join(', ')}</div>
      </div>
    );
  }

  const keys = Object.keys(rows[0]).filter(k => !HIDDEN_KEYS.has(k)
    && !Array.isArray(rows[0][k]) && (rows[0][k] === null || typeof rows[0][k] !== 'object')).slice(0, 8);
  const labelKey = keys.find(k => typeof rows[0][k] === 'string' && !isDateField(k, rows[0][k])) || keys[0];
  const numKey = keys.find(k => isPlainNum(rows[0][k]) && valueKind(k, rows[0][k]) === 'money')
    || keys.find(k => isPlainNum(rows[0][k]) && k !== labelKey);
  const showChart = !!(labelKey && numKey && rows.length > 1 && rows.length <= 24 && labelKey !== numKey);

  const pageCount = Math.ceil(rows.length / ROWS_PER_PAGE);
  const shown = rows.slice(page * ROWS_PER_PAGE, (page + 1) * ROWS_PER_PAGE);

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 10, marginBottom: 8 }}>
        <div style={{ fontSize: 12.5, fontWeight: 700, color: T.text }}>{titleCase(sectionOf(name))}</div>
        <div style={{ fontSize: 10.5, color: T.textDim }}>{fmtN(rows.length)} {t('kayıt')}</div>
      </div>
      {showChart && (
        <ResponsiveContainer width="100%" height={isMobile ? 180 : 210}>
          <BarChart data={rows.slice(0, 20)} margin={{ top: 8, right: 8, left: -14, bottom: 0 }}>
            <XAxis dataKey={labelKey} tick={{ fontSize: 9, fill: T.textDim }} axisLine={false} tickLine={false}
              tickFormatter={(v: any) => String(v).length > 12 ? String(v).slice(0, 11) + '…' : String(v)} />
            <YAxis tick={{ fontSize: 9, fill: T.textDim }} axisLine={false} tickLine={false} />
            <Tooltip formatter={(v: any) => fmtVal(numKey as string, v, currency)}
              labelFormatter={(l: any) => `${titleCase(labelOf(labelKey as string))}: ${l}`} />
            <Bar dataKey={numKey as string} fill={T.accent} radius={[4, 4, 0, 0]} name={titleCase(labelOf(numKey as string))} />
          </BarChart>
        </ResponsiveContainer>
      )}
      <div style={{ overflowX: 'auto' as const, marginTop: showChart ? 10 : 0 }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' as const, fontSize: 11.5 }}>
          <thead><tr>{keys.map((k, i) => (
            <th key={k} style={{ padding: '7px 10px', textAlign: i === 0 ? 'left' as const : 'right' as const, color: T.textDim, fontSize: 9, textTransform: 'uppercase' as const, letterSpacing: '0.07em', borderBottom: `1px solid ${T.border}`, whiteSpace: 'nowrap' as const }}>{labelOf(k)}</th>
          ))}</tr></thead>
          <tbody>{shown.map((r: any, i: number) => (
            <tr key={i}>{keys.map((k, ki) => (
              <td key={k} style={{ padding: '7px 10px', color: ki === 0 ? T.text : T.textMid, textAlign: ki === 0 ? 'left' as const : 'right' as const, borderBottom: `1px solid ${T.card}`, fontVariantNumeric: 'tabular-nums' }}>
                {isImageField(k, r[k])
                  ? <img src={r[k]} alt="" width={28} height={28} loading="lazy" referrerPolicy="no-referrer"
                      style={{ borderRadius: 6, objectFit: 'cover' as const, background: T.card }} />
                  : fmtVal(k, r[k], currency)}
              </td>
            ))}</tr>
          ))}</tbody>
        </table>
      </div>
      {pageCount > 1 && (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, paddingTop: 10 }}>
          <div style={{ fontSize: 10.5, color: T.textDim }}>
            {page * ROWS_PER_PAGE + 1}–{Math.min((page + 1) * ROWS_PER_PAGE, rows.length)} / {fmtN(rows.length)}
          </div>
          <div style={{ display: 'flex', gap: 6 }}>
            <Button variant="ghost" color={T.accent} disabled={page === 0} onClick={() => setPage(p => p - 1)} style={{ padding: '4px 10px', fontSize: 10.5 }}>← {t('Önceki')}</Button>
            <Button variant="ghost" color={T.accent} disabled={page >= pageCount - 1} onClick={() => setPage(p => p + 1)} style={{ padding: '4px 10px', fontSize: 10.5 }}>{t('Sonraki')} →</Button>
          </div>
        </div>
      )}
    </div>
  );
}

function ObjectSection({ name, obj, currency, isMobile, enums, depth = 0 }: {
  name: string; obj: any; currency: string; isMobile: boolean; enums?: any; depth?: number;
}) {
  const nums = Object.entries(obj).filter(([k, v]) => !HIDDEN_KEYS.has(k) && (isPlainNum(v) || typeof v === 'boolean' || typeof v === 'string'));
  const arrays = Object.entries(obj).filter(([k, v]) => !HIDDEN_KEYS.has(k) && Array.isArray(v) && (v as any[]).length);
  const objs = Object.entries(obj).filter(([k, v]) => !HIDDEN_KEYS.has(k) && v && typeof v === 'object' && !Array.isArray(v));
  if (!nums.length && !arrays.length && !objs.length) return null;

  return (
    <div style={{ borderTop: depth === 0 ? `1px solid ${T.border}` : 'none', paddingTop: depth === 0 ? 16 : 0 }}>
      <div style={{ fontSize: 12.5, fontWeight: 700, color: T.text, marginBottom: 10 }}>{titleCase(sectionOf(name))}</div>
      {nums.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap' as const, gap: 18, marginBottom: arrays.length || objs.length ? 14 : 0 }}>
          {nums.map(([k, v]) => (
            <div key={k} style={{ maxWidth: typeof v === 'string' && String(v).length > 40 ? 520 : undefined }}>
              <div style={{ fontSize: 10, color: T.textDim, marginBottom: 2 }}>{titleCase(labelOf(k))}</div>
              <div style={{ fontSize: typeof v === 'string' && String(v).length > 40 ? 12.5 : 14, fontWeight: typeof v === 'string' && String(v).length > 40 ? 500 : 700,
                color: typeof v === 'string' && String(v).length > 40 ? T.textMid : T.text,
                fontFamily: typeof v === 'string' && !isPlainNum(v) ? 'inherit' : MONO, lineHeight: 1.5 }}>
                {fmtVal(k, v, currency)}
              </div>
            </div>
          ))}
        </div>
      )}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        {arrays.map(([k, v]) => <ArraySection key={k} name={k} rows={v as any[]} currency={currency} isMobile={isMobile} enums={enums} />)}
        {objs.map(([k, v]) => <ObjectSection key={k} name={k} obj={v} currency={currency} isMobile={isMobile} enums={enums} depth={depth + 1} />)}
      </div>
    </div>
  );
}

function ReportView({ data, currency, isMobile, title, aggregation, enums }: {
  data: any; currency: string; isMobile: boolean; title: string; aggregation?: any; enums?: any;
}) {
  const { t } = useLang();
  if (data === null || data === undefined) {
    return <Card pad={28}><div style={{ textAlign: 'center' as const, fontSize: 12.5, color: T.textDim }}>{t('Kayıt bulunamadı.')}</div></Card>;
  }

  if (Array.isArray(data)) {
    if (!data.length) {
      return <Card pad={28}><div style={{ textAlign: 'center' as const, fontSize: 12.5, color: T.textDim }}>{t('Bu dönem için veri yok.')}</div></Card>;
    }
    return <Card pad={20}><ArraySection name={title} rows={data} currency={currency} isMobile={isMobile}
      aggregation={aggregation} enums={enums} /></Card>;
  }

  const metricsRaw = (data as any).metrics;
  const metrics = Array.isArray(metricsRaw) ? metricsRaw[0] : metricsRaw;
  const flatNums = Object.fromEntries(Object.entries(data).filter(([k, v]) => !HIDDEN_KEYS.has(k) && k !== 'metrics' && k !== 'charts' && isPlainNum(v)));
  const summary = { ...(metrics && typeof metrics === 'object' ? metrics : {}), ...flatNums };
  const charts = (data as any).charts;
  const rest = Object.entries(data).filter(([k, v]) =>
    !HIDDEN_KEYS.has(k) && !['metrics', 'charts', 'currency'].includes(k) && v && typeof v === 'object');

  if (!Object.keys(summary).length && !charts && !rest.length) {
    return <Card pad={28}><div style={{ textAlign: 'center' as const, fontSize: 12.5, color: T.textDim }}>{t('Bu dönem için veri yok.')}</div></Card>;
  }

  // Maliyet girilmemişse kâr/marj rakamları yanıltıcı: sistem maliyeti sıfır sayıp
  // neredeyse tüm ciroyu kâr gösteriyor. Ekranda açıkça uyarıyoruz.
  const noCost = Number(summary.grossRevenue || summary.totalRevenue || 0) > 0
    && Number(summary.productCost || 0) === 0
    && ('grossProfit' in summary || 'profitMargin' in summary);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      {noCost && (
        <Card pad={14}>
          <div style={{ fontSize: 12, color: T.amberText, lineHeight: 1.7 }}>
            <b>{t('Ürün maliyetleri Pixa\'da girilmemiş.')}</b>{' '}
            {t('Bu yüzden kâr, marj ve net tutar rakamları gerçeği yansıtmıyor — sistem maliyeti sıfır sayıp cironun tamamına yakınını kâr gösteriyor. "Net tutar (tüm kanallar)" kalemi pazaryeri hakedişi değildir; Shopify dahil tüm satış kanallarının cirosundan iade, iptal ve komisyon düşülmüş hâlidir.')}
          </div>
        </Card>
      )}
      {Object.keys(summary).length > 0 && <MetricCards obj={summary} currency={currency} isMobile={isMobile} />}
      {(charts || rest.length > 0) && (
        <Card pad={20}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
            {charts && typeof charts === 'object' && Object.entries(charts)
              .filter(([k, v]) => v && (Array.isArray(v) ? (v as any[]).length : typeof v === 'object'))
              .map(([k, v]) => Array.isArray(v)
                ? <ArraySection key={k} name={k} rows={v as any[]} currency={currency} isMobile={isMobile} enums={enums} />
                : <ObjectSection key={k} name={k} obj={v} currency={currency} isMobile={isMobile} enums={enums} />)}
            {rest.map(([k, v]) => Array.isArray(v)
              ? <ArraySection key={k} name={k} rows={v as any[]} currency={currency} isMobile={isMobile} enums={enums} />
              : <ObjectSection key={k} name={k} obj={v} currency={currency} isMobile={isMobile} enums={enums} />)}
          </div>
        </Card>
      )}
    </div>
  );
}
